import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';

export default function WebhookTest() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{
    success?: boolean;
    message?: string;
    error?: string;
  } | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    
    try {
      console.log('Starting webhook test...');
      
      // Check if webhook URL is configured
      const webhookUrl = import.meta.env.VITE_SLACK_WEBHOOK_URL;
      if (!webhookUrl) {
        setTimeout(() => {
          setResult({
            success: false,
            error: 'Slack webhook URL is not configured. Please add it to your .env file.'
          });
          setTesting(false);
        }, 1000);
        return;
      }
      
      // Send a test message to Slack
      const testPayload = {
        text: "🧪 Webhook Test from Survey App",
        blocks: [
          {
            type: "header",
            text: {
              type: "plain_text",
              text: "🧪 Webhook Test",
              emoji: true
            }
          },
          {
            type: "section",
            text: {
              type: "mrkdwn",
              text: "This is a test message from the Survey App. If you're seeing this, the webhook is working correctly!"
            }
          },
          {
            type: "context",
            elements: [
              {
                type: "mrkdwn",
                text: `Test sent at: ${new Date().toLocaleString()}`
              }
            ]
          }
        ]
      };

      console.log('Sending test notification with payload:', JSON.stringify(testPayload));
      console.log('Using webhook URL:', webhookUrl);

      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(testPayload)
        });

        if (response.ok) {
          console.log('Test notification sent successfully!');
          setResult({
            success: true,
            message: 'Test notification sent successfully! Check your Slack channel.'
          });
        } else {
          const errorText = await response.text();
          console.error(`Failed to send notification: Status ${response.status} - ${errorText}`);
          
          let errorMessage = `Failed to send notification: ${response.statusText}`;
          
          // Provide more helpful error messages for common status codes
          if (response.status === 410) {
            errorMessage = 'The webhook URL is no longer valid. Please generate a new webhook URL in Slack.';
          } else if (response.status === 404) {
            errorMessage = 'The webhook URL was not found. Please check that you have the correct URL.';
          } else if (response.status === 403) {
            errorMessage = 'Access to the webhook URL is forbidden. Please check your permissions.';
          }
          
          setResult({
            success: false,
            error: errorMessage
          });
        }
      } catch (error) {
        console.error('Error sending test notification:', error);
        setResult({
          success: false,
          error: error instanceof Error ? error.message : 'Failed to send notification'
        });
      }
      
      setTesting(false);
    } catch (error) {
      console.error('Error in webhook test:', error);
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      setTesting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white shadow-md rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Slack Webhook Test</h2>
      <p className="text-sm text-gray-600 mb-4">
        This will attempt to send a test message to the configured Slack webhook.
        Check the browser console for detailed logs.
      </p>
      
      <button
        onClick={handleTest}
        disabled={testing}
        className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D] transition-colors duration-200 ${
          testing ? 'opacity-75 cursor-not-allowed' : ''
        }`}
      >
        {testing ? (
          <>
            <Loader className="animate-spin h-4 w-4 mr-2" />
            Testing...
          </>
        ) : 'Test Webhook'}
      </button>

      {result && (
        <div className={`mt-4 p-4 rounded-md ${
          result.success 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            {result.success ? (
              <CheckCircle className="h-5 w-5 text-green-400" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${
                result.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.success ? 'Success' : 'Error'}
              </h3>
              <div className={`mt-2 text-sm ${
                result.success ? 'text-green-700' : 'text-red-700'
              }`}>
                {result.success ? result.message : result.error}
              </div>
              
              {!result.success && (
                <div className="mt-4 text-sm text-red-700">
                  <p className="font-medium">Troubleshooting steps:</p>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Verify that your Slack webhook URL in the .env file is correct and up-to-date</li>
                    <li>Check if the webhook has been deactivated in your Slack workspace</li>
                    <li>Create a new webhook URL in Slack if the current one is no longer valid</li>
                    <li>Ensure your network connection allows outbound requests to Slack's servers</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
        <h3 className="text-sm font-medium text-gray-800 mb-2">Webhook Configuration</h3>
        <p className="text-sm text-gray-700">
          To configure the Slack webhook, add the following to your <code>.env</code> file:
        </p>
        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
          VITE_SLACK_WEBHOOK_URL=https://hooks.slack.com/services/your-webhook-path
        </pre>
        <p className="text-sm text-gray-700 mt-2">
          <strong>Important:</strong> If your webhook is no longer working, you may need to create a new one in your Slack workspace.
        </p>
      </div>
    </div>
  );
}